"use client"

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPetTypeCollection } from '@/app/actions';
import Image from 'next/image';
import SearchSelectBreed from './SearchSelectBreed'; // Importing SearchSelectBreed component
import SearchSelectLocation from './SearchSelectLocation'; // Importing SearchSelectLocation component
import { useRouter } from 'next/navigation'; // Importing useRouter for navigation
import { getFileUrl } from '@/utils/data';

const Button = ({ icon, text, isActive, onClick }: {icon: string, text: string, isActive: boolean, onClick: () => void} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const btnClass = isActive ? "btn btn-outline no-animation border-primary hover:border-primary" : "btn btn-outline no-animation";
  return (
    <button onClick={onClick} className={`${btnClass} btn-square w-fit sm:h-24 sm:w-32 normal-case h-full sm:aspect-auto overflow-hidden flex flex-row items-center sm:py-2 hover:bg-inherit border-2 ${isActive ? 'border-primary' : 'border-base-content/20'} p-1`}>
      <div className="flex flex-col items-center justify-center w-full h-full">
        <Image src={icon} alt={text} width={40} height={40} className="w-10 h-10 mb-2" />
        <p className='w-20 font-thin text-secondary-content/60 overflow-hidden text-ellipsis text-center sm:w-full capitalize text-lg'>{text}</p>
      </div>
    </button>
  );
};

const PetServiceSearch = () => {
  const [activeButton, setActiveButton] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [otherIcon, setOtherIcon] = useState(getFileUrl('commodityType', '48tvnn9afkkuqds', 'pets_svgrepo_xgzAKGQGA6.com.png'));
  const [otherText, setOtherText] = useState('Other ▼');
  const [selectedBreed, setSelectedBreed] = useState(''); // State to hold selected breed
  const [selectedLocation, setSelectedLocation] = useState<any>({}); // State to hold selected location

  const router = useRouter(); // Initialize router

  const { data: petTypes = [], isLoading } = useQuery({
    queryKey: ['petTypeCollection'],
    queryFn: getPetTypeCollection
  });

  const mainTypes = ['cat', 'dog'];
  const otherTypes = petTypes.filter((type: any) => !mainTypes.includes(type.name.toLowerCase()));

  if (isLoading) {
    return <></>
  }

  const handleSearch = () => {
    if (!activeButton && !(selectedLocation?.name)) {
      router.push('/sale/pets');
      return;
    }

    // If only location is selected, use the near-me route
    if (!activeButton && selectedLocation?.name) {
      const {name, postcode} = selectedLocation;
      const formattedLocation = name
      const formattedPostcode = postcode
      const path = `sale/pets/near-me/${formattedLocation}-${formattedPostcode}`
      router.push(path);
      return;
    }

    const petTypeObj = petTypes.find((pt: any) => pt.name.toLowerCase() === activeButton.toLowerCase());
    const petType = petTypeObj ? petTypeObj.pluralName.toLowerCase() : '';

    // If petType and location are selected, use near-me route
    if (petType && !!selectedLocation?.name && !selectedBreed) {
      const {name, postcode} = selectedLocation;
      const formattedLocation = name
      const formattedPostcode = postcode;
      const path = `/sale/${petType}/${formattedLocation}-${formattedPostcode}`
      router.push(path);
      return;
    }

    // Otherwise use breed-based route
    const breed = selectedBreed ? selectedBreed.toLowerCase().replace(/ /g, '-') : '';
    const {name, postcode} = selectedLocation;
    const path = `/sale/${petType}${breed ? `/${breed}` : ''}${!!location && !!postcode ? `/${name}-${postcode}` : ''}`;
    router.push(path);
  };

  return (
    <div className="bg-base-100 p-6 rounded-xl max-w-xl w-fit flex flex-col gap-2">
      <div className="flex gap-2 w-fit">
        {mainTypes.map((type) => {
          const petType = petTypes.find((pt: any) => pt.name.toLowerCase() === type);
          return (
            <Button 
              key={type}
              icon={getFileUrl(petType?.collectionId || '', petType?.id || '', petType?.icon || '')}
              text={petType?.pluralName || type} 
              isActive={activeButton === type} 
              onClick={() => {
                setActiveButton(type);
                setSelectedBreed(''); // Clear the selected breed when pet type changes
                setShowDropdown(false);
                setOtherIcon(getFileUrl('commodityType', '48tvnn9afkkuqds', 'pets_svgrepo_xgzAKGQGA6.com.png'));
                setOtherText('Other ▼');
              }}
            />
          );
        })}
        <div className="relative">
          <Button 
            icon={otherIcon}
            text={otherText}
            isActive={activeButton === 'Other'} 
            onClick={() => setShowDropdown(!showDropdown)}
          />
          {showDropdown && (
            <div className="bg-white shadow-md rounded-lg p-4 absolute top-full mt-2 z-40">
              {otherTypes.map((type: any) => (
                <div 
                  key={type.name} 
                  className="cursor-pointer p-2 hover:bg-gray-200" 
                  onClick={() => {
                    setActiveButton('Other');
                    setSelectedBreed(''); // Clear the selected breed when pet type changes
                    setOtherIcon(getFileUrl(type.collectionId, type.id, type.icon));
                    setOtherText(type.pluralName || type.name);
                    setShowDropdown(false);
                  }}
                >
                  {type.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SearchSelectBreed petType={activeButton} setBreed={setSelectedBreed} />
      <div className="flex gap-2 items-center">
        <SearchSelectLocation setLocation={setSelectedLocation} />
        <button onClick={handleSearch} className="btn btn-primary flex-shrink-0 rounded-full capitalize">Search</button>
      </div>
    </div>
  );
};

export default PetServiceSearch;